import { CodeXml, Images, Info } from "lucide-react";
import React, { useState } from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgCodeEditor } from "@intelligems/ig-theme/IgCodeEditor";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgLink } from "@intelligems/ig-theme/IgLink";
import { IgToggleButtonGroup } from "@intelligems/ig-theme/IgToggleButtonGroup";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ImageEditMode } from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { ImageMod } from "@src/store/slices/experienceBuilder/types";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
  ToggleButton,
  useTheme,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { ImageLibraryDialog } from "@src/components/Dialogs/ImageLibraryDialog";
import { URLInputDialog } from "@src/features/experienceBuilder/modifications/basicMods/URLInputDialog";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { useDialog } from "@src/hooks/dialog";
import { useDispatch, useSelector, useSelectorFactory } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { assert } from "@src/utils/assert";
import { ElementSelector } from "@src/features/experienceBuilder/modifications/basicMods/ElementSelector";
import { ElementMeta } from "@src/features/experienceBuilder/modifications/common/panel/ElementMeta";

const OriginalImage = styled("img")(({ theme }) => ({
  width: 100,
  height: 100,
  backgroundColor: theme.igBackgroundColors.interactive.neutral.default,
  border: `1px solid ${theme.igBorders.core.default}`,
  borderRadius: "6px",
  flexShrink: 0,
  objectFit: "cover",
}));

const CodeEditorWrapper = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.igBorders.core.default}`,
  borderRadius: "6px",
}));

const imageEditModeButtons = [
  <ToggleButton value="image" key="image" size="small" sx={{ flex: 1, gap: 1 }}>
    <Images size={16} />
    Edit image
  </ToggleButton>,
  <ToggleButton value="html" key="html" size="small" sx={{ flex: 1, gap: 1 }}>
    <CodeXml size={16} />
    Edit HTML
  </ToggleButton>,
];

export const ImagePanel = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const imageLibraryDialog = useDialog("imageLibrary");
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const errorColor = theme.palette.text.tonal.error;

  const mod = useSelector(Selector.basicMod.getVariationDraftImage);
  assert(mod !== undefined, "mod is undefined");

  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const src = useSelectorFactory(Selector.getOriginalSrcFactory, mod);
  const errors = useSelector(Selector.getVariationDraftUIErrors);

  const update = (update: Partial<ImageMod>) =>
    dispatch(Action.basicMod.setDraft({ ...mod, ...update }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());
  const originalSrc = mod.imageSearchUrl ?? src;

  const handleSelectImage = () => {
    imageLibraryDialog.open({
      id: imageLibraryDialog.id,
      onUpdateImage: (src) => update({ src }),
    });
  };

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          {originalSrc && (
            <Stack
              direction="row"
              spacing={5}
              alignItems="center"
              sx={{
                border: `1px solid ${theme.igBorders.core.default}`,
                borderRadius: "6px",
                padding: 4,
              }}
            >
              <OriginalImage src={src} />
              <IgTypography variant="body1">Original image</IgTypography>
            </Stack>
          )}

          <ElementMeta mod={mod} errors={errors} onChange={update} />

          <IgDivider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Panel.Header>Image change</Panel.Header>
            <Panel.Checkbox
              label="Hide element"
              checked={mod.remove}
              onChange={(remove) => update({ remove })}
            />
          </Stack>

          {!mod.remove && (
            <>
              <IgToggleButtonGroup
                buttons={imageEditModeButtons}
                value={mod.editMode}
                sx={{ width: "100%" }}
                control={{
                  exclusive: true,
                  onChange: (_event, editMode: ImageEditMode) => {
                    if (editMode) {
                      update({ editMode });
                    }
                  },
                }}
              />

              {mod.editMode === "image" ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={3}
                  sx={{
                    border: `1px solid ${errors?.src ? errorColor : theme.igBorders.core.default}`,
                    borderRadius: "8px",
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.default,
                    minHeight: 200,
                    padding: 5,
                  }}
                >
                  {mod.src ? (
                    <>
                      <img
                        src={mod.src}
                        alt="Selected"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 140,
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                      <IgTypography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: "100%" }}
                      >
                        {mod.src.split("/").pop()}
                      </IgTypography>
                      <IgButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={handleSelectImage}
                      >
                        Change image
                      </IgButton>
                    </>
                  ) : (
                    <>
                      <IgButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={handleSelectImage}
                      >
                        Browse library
                      </IgButton>
                      <IgTypography variant="body1" color="text.secondary">
                        or{" "}
                        <IgLink
                          component="button"
                          onClick={() => setUrlDialogOpen(true)}
                          sx={{ verticalAlign: "baseline" }}
                        >
                          add an image URL
                        </IgLink>
                      </IgTypography>
                    </>
                  )}
                </Stack>
              ) : (
                <CodeEditorWrapper>
                  <IgCodeEditor
                    language="html"
                    height="200px"
                    value={mod.html}
                    onChange={(html) => update({ html })}
                  />
                </CodeEditorWrapper>
              )}
              {src && (
                <>
                  <IgDivider />

                  <Stack spacing={4}>
                    <Panel.Header>Apply image replacement to</Panel.Header>
                    <RadioGroup
                      row
                      value={mod.imageSearchUrl ? "all" : "this"}
                      onChange={(e) =>
                        update({
                          imageSearchUrl: e.target.value === "all" ? src : null,
                        })
                      }
                    >
                      <FormControlLabel
                        value="this"
                        control={<Radio size="small" />}
                        label="This image only"
                        sx={{ marginRight: 4 }}
                      />
                      <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label="All instances of this image"
                      />
                    </RadioGroup>
                  </Stack>
                </>
              )}
            </>
          )}
        </Panel.Body>
        <Panel.Buttons>
          <Panel.CancelButton onClick={cancel} />
          <Panel.ApplyButton onClick={apply} disabled={!isApplicable} />
        </Panel.Buttons>
      </Panel>

      <ImageLibraryDialog id={imageLibraryDialog.id} />

      <URLInputDialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        onApply={(src) => update({ src })}
      />
    </>
  );
};
